import { useCallback, useSyncExternalStore } from 'react';

/**
 * Ticket #37 — Store de tri partagé (pattern `useFilterStore`).
 *
 * Le tri des écrans « Personnes » (`PersonsView`) et « Planning »
 * (`ScheduleView`, partagé avec la saisie des temps calendaire) vivait dans un
 * `useState` local + une écriture `localStorage`. Les **vues sauvegardées**
 * doivent pouvoir le lire ET l'écrire depuis un composant tiers
 * (`SavedViewsControl`, monté dans la `FilterBar`) : on externalise donc l'état
 * dans un store partagé par clé, exactement comme le filtre facetté.
 *
 * Forme du tri : `{ field, dir: 'asc' | 'desc' }`.
 *
 *   const { sort, setSort } = useSortStore(SCHEDULE_PERSON_SORT);
 */

const stores = new Map();

/** Descripteur d'un tri : clé localStorage + valeur par défaut + parsing. */
export const PERSONS_SORT = {
  key: 'synolog_persons_sort',
  fallback: { field: 'lastName', dir: 'asc' },
};

export const AFFAIRES_SORT = {
  key: 'synolog_affaires_sort',
  fallback: { field: 'name', dir: 'asc' },
};

// synotis-support#13 — rapport « Projets » : liste et heatmap des personnes.
export const PROJECTS_REPORT_SORT = {
  key: 'synolog_projects_report_sort',
  fallback: { field: 'consumptionPct', dir: 'desc' },
};

export const PROJECT_PEOPLE_SORT = {
  key: 'synolog_project_people_sort',
  fallback: { field: 'periodHours', dir: 'desc' },
};

// synotis-support#34 — imputations d'un projet (tri appliqué côté serveur).
export const PROJECT_TIME_ENTRIES_SORT = {
  key: 'synolog_project_time_entries_sort',
  fallback: { field: 'date', dir: 'desc' },
};

// Ticket #56 / #113 — Accueil, « Suivi du temps par affaire ».
export const AFFAIRE_TIME_SORT = {
  key: 'synolog_home_affaire_time_sort',
  fallback: { field: 'periodHours', dir: 'desc' },
};

export const SCHEDULE_PERSON_SORT = {
  key: 'synolog_schedule_person_sort',
  fallback: { field: 'lastName', dir: 'asc' },
  // Rétro-compatibilité v1 : l'ancienne valeur était la chaîne brute
  // 'asc' / 'desc', promue ici en `{ field: 'lastName', dir }`.
  parseLegacy: (raw) =>
    raw === 'asc' || raw === 'desc' ? { field: 'lastName', dir: raw } : null,
};

/**
 * Le tri mémorisé par une vue sauvegardée dépend du scope de filtre : c'est le
 * tri de l'écran qui porte cette `FilterBar`. Les scopes sans colonne triable
 * (`logtime`, `timeoffs`) retournent `null` — la vue ne mémorise alors aucun
 * tri.
 */
export function sortDescriptorForScope(scope) {
  if (scope === 'persons') return PERSONS_SORT;
  if (scope === 'schedule') return SCHEDULE_PERSON_SORT;
  if (scope === 'affaires') return AFFAIRES_SORT;
  return null;
}

function normalize(value, fallback) {
  if (!value || typeof value !== 'object') return fallback;
  const field = typeof value.field === 'string' && value.field ? value.field : fallback.field;
  const dir = value.dir === 'desc' ? 'desc' : 'asc';
  return { field, dir };
}

function loadFromStorage(descriptor) {
  try {
    const raw = localStorage.getItem(descriptor.key);
    if (!raw) return descriptor.fallback;
    const legacy = descriptor.parseLegacy?.(raw);
    if (legacy) return legacy;
    return normalize(JSON.parse(raw), descriptor.fallback);
  } catch {
    return descriptor.fallback;
  }
}

function getStore(descriptor) {
  let store = stores.get(descriptor.key);
  if (!store) {
    let state = loadFromStorage(descriptor);
    const listeners = new Set();
    store = {
      getSnapshot: () => state,
      subscribe: (cb) => {
        listeners.add(cb);
        return () => listeners.delete(cb);
      },
      setState: (next) => {
        state = normalize(next, descriptor.fallback);
        try {
          localStorage.setItem(descriptor.key, JSON.stringify(state));
        } catch {
          // localStorage indisponible (mode privé) — l'état reste en mémoire.
        }
        listeners.forEach((cb) => cb());
      },
    };
    stores.set(descriptor.key, store);
  }
  return store;
}

export function useSortStore(descriptor) {
  const store = getStore(descriptor);
  const sort = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  // Accepte une valeur ou un updater `(prev) => next` (compatible `setState`).
  const setSort = useCallback(
    (next) => {
      store.setState(typeof next === 'function' ? next(store.getSnapshot()) : next);
    },
    [store],
  );

  return { sort, setSort };
}

/** Lecture/écriture hors composant (ex. application d'une vue sauvegardée). */
export function readSort(descriptor) {
  return getStore(descriptor).getSnapshot();
}

export function writeSort(descriptor, value) {
  getStore(descriptor).setState(value);
}
